import { useEffect, RefObject } from 'react';

/**
 * Calls `onOutside` when a pointerdown occurs outside the referenced element,
 * or when the Escape key is pressed. Used to dismiss menus, popovers, and
 * dropdowns that aren't backed by a full modal overlay.
 *
 * @param ref - Ref to the container that should stay "inside".
 * @param onOutside - Handler fired on an outside click or Escape.
 * @param enabled - When false the listeners are not attached (default true).
 */
export function useClickOutside<T extends HTMLElement>(
  ref: RefObject<T>,
  onOutside: () => void,
  enabled: boolean = true,
): void {
  useEffect(() => {
    if (!enabled) return;

    const handlePointer = (event: MouseEvent | TouchEvent) => {
      const el = ref.current;
      if (el && !el.contains(event.target as Node)) {
        onOutside();
      }
    };

    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onOutside();
    };

    document.addEventListener('mousedown', handlePointer);
    document.addEventListener('touchstart', handlePointer);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handlePointer);
      document.removeEventListener('touchstart', handlePointer);
      document.removeEventListener('keydown', handleKey);
    };
  }, [ref, onOutside, enabled]);
}

export default useClickOutside;
